import { useTranslation } from 'react-i18next';
import Password from '@components/FormInputs/Password';
import Align from '@components/UI/utilities/Align';
import { Button, Form, FormInstance } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import ConfirmPassword from '@components/FormInputs/ConfirmPassword';
import focusFirstFormError from '@lib/focusFirstFormError';
import styled from '@emotion/styled';

type NewPasswordViewProps = {
  /** The Ant Design form instance */
  form: FormInstance<any>;
  /** A submit handler for the new password form */
  onFinish: (values: any) => void;
  /** Loading state */
  loading: boolean;
  /** The username of the user */
  username: string;
};

const StyledWrapper = styled.div`
  border: 1px solid rgba(227, 232, 239, 0.584);
  border-radius: 24px;
  box-shadow: rgba(100, 100, 111, 0.2) 0 7px 29px 0;
  padding: 24px 24px 0;
  background: #fff;
  margin: 0 auto;
  width: min(600px, 100%);
`;

const NewPasswordView: React.FC<NewPasswordViewProps> = ({ form, onFinish, loading, username }) => {
  const { t } = useTranslation('common');
  const [password, setPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);

  const onConfirmPasswordChanged = useCallback(
    (value: string) => {
      form.setFields([
        {
          name: 'confirmpassword',
          value
        }
      ]);
    },
    [form]
  );
  const onPasswordChanged = useCallback(
    (val: string) => {
      form.setFieldsValue({
        password: val
      });
      setPassword(val);
    },
    [form]
  );

  useEffect(() => {
    if (passwordValid && form.getFieldError('password').length > 0) {
      form.validateFields(['password']);
    }
  }, [passwordValid, form]);

  return (
    <StyledWrapper>
      <h2>{t('Please now set a password of your choice.')}</h2>
      <p>
        {t('Email address: ')}
        <span>{username}</span>
      </p>

      <Form layout="vertical" form={form} onFinishFailed={focusFirstFormError(form)} {...{ onFinish }}>
        <Form.Item
          label={t('New Password')}
          name="password"
          validateTrigger="onBlur"
          rules={[
            {
              validator(_, value) {
                if (!value) {
                  return Promise.reject(new Error('Please input your password!'));
                }
                if (!passwordValid) {
                  return Promise.reject(new Error('Please enter a valid password!'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Password onChange={onPasswordChanged} setValid={setPasswordValid} />
        </Form.Item>

        <Form.Item
          label={t('Confirm New Password')}
          name="confirmpassword"
          dependencies={['password']}
          rules={[
            {
              required: true,
              message: 'Please confirm your password!'
            },
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('The two passwords that you entered do not match!'));
              }
            })
          ]}
        >
          <ConfirmPassword onChange={onConfirmPasswordChanged} password={password} />
        </Form.Item>

        <Form.Item>
          <Align x="right">
            <Button type="primary" size="large" htmlType="submit" {...{ loading }} data-testid="sign-in">
              {t('Sign in')}
            </Button>
          </Align>
        </Form.Item>
      </Form>
    </StyledWrapper>
  );
};

export default NewPasswordView;
